import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ModalDialog from 'app/components/flat/ui/ModalDialog';
import { useOabEligibility } from 'app/services/oabEligibility';
import {
  trackOabCaptureDismissed,
  trackOabCaptureSurfaceViewed,
  trackOabCaptureTriggerSuppressed,
} from 'app/services/tracking/oabCapture/oabCaptureEvents';

import OabRegistrationModal from '../OabRegistrationModal';
import OabThanksModal from '../OabThanksModal';
import { useOabCaptureTrigger } from '../OabCaptureTriggerContext';
import {
  OAB_CAPTURE_ANSWER_ERROR,
  useOabCaptureAnswer,
} from '../useOabCaptureAnswer';
import { useOabCaptureShownMarks } from '../useOabCaptureShownMarks';
import illustration from '../modal-illustration.svg';
import * as S from '../modalStyles';

const SURFACE = 'value_moment_modal' as const;
const SESSION_SHOWN_KEY = 'oabCaptureValueMomentModalShown';
const TITLE_ID = 'oab-capture-value-moment-title';

// Acima do maior atraso de CSAT das superfícies-alvo (10s em Revisional, Divórcio,
// Dosimetria e Progressão de Regime): abrir antes disso deixaria o CSAT surgir por cima.
export const VALUE_MOMENT_MODAL_DELAY_MS = 12000;

type CsatState = { app?: { is_csat_modal_open?: boolean } };

type Answer = 'lawyer' | 'other';

const wasShownInThisSession = (): boolean => {
  try {
    return window.sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
  } catch {
    return false;
  }
};

const rememberShownInThisSession = (): void => {
  try {
    window.sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  } catch {
    /* sessão sem storage: o teto degrada para "uma vez por montagem" */
  }
};

const OabCaptureValueMomentModal = (): React.ReactElement | null => {
  const { armedTrigger } = useOabCaptureTrigger();
  const wasShownBeforeMount = useRef(wasShownInThisSession());
  const isArmed = armedTrigger !== null && !wasShownBeforeMount.current;

  const {
    isQuestionEligible,
    isEligibilityResolved,
    answerProfession,
    isAnsweringProfession,
    markBannerShown,
  } = useOabEligibility({ enabled: isArmed });
  const isCsatModalOpen = useSelector(
    (state: CsatState) => state.app?.is_csat_modal_open,
  );
  const { recordShown } = useOabCaptureShownMarks({
    surface: SURFACE,
    step: 'question',
    markBannerShown,
  });

  const [hasDelayElapsed, setHasDelayElapsed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isShowing, setIsShowing] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const hasRecordedDisplay = useRef(false);
  const hasRecordedSuppression = useRef(false);

  // isShowing latches the open state: the display closes the eligibility window
  // for every surface, and without the latch the modal would close itself on the
  // render right after opening — with the OAB form already on screen.
  const isOpen =
    isArmed &&
    (isQuestionEligible || isShowing) &&
    hasDelayElapsed &&
    !isCsatModalOpen &&
    !isDismissed;

  useEffect(() => {
    if (!isArmed) {
      return undefined;
    }

    const timer = setTimeout(
      () => setHasDelayElapsed(true),
      VALUE_MOMENT_MODAL_DELAY_MS,
    );

    return () => clearTimeout(timer);
  }, [isArmed]);

  useEffect(() => {
    if (!isOpen || hasRecordedDisplay.current) {
      return;
    }

    hasRecordedDisplay.current = true;
    setIsShowing(true);
    rememberShownInThisSession();
    trackOabCaptureSurfaceViewed({
      surface: SURFACE,
      step: 'question',
      triggerEvent: armedTrigger,
    });
    recordShown();
  }, [isOpen, armedTrigger, recordShown]);

  // O motivo em ordem de precedência: já exibida nesta sessão dispensa a
  // consulta de elegibilidade (que nem chega a rodar, ver isArmed); os outros
  // dois só se decidem depois que a espera e a consulta terminaram — o que
  // vier por último — senão gente elegível ainda em voo cairia no balde
  // errado.
  // hasRecordedDisplay guards a real display: recordShown() flips
  // shouldShowBanner/isQuestionEligible to false, and isOpen turns false again
  // on dismiss — without this guard, the effect reruns on dismiss and reports
  // a spurious not_eligible for a trigger that already showed.
  useEffect(() => {
    if (
      armedTrigger === null ||
      hasRecordedSuppression.current ||
      hasRecordedDisplay.current ||
      isOpen
    ) {
      return;
    }

    if (wasShownBeforeMount.current) {
      hasRecordedSuppression.current = true;
      trackOabCaptureTriggerSuppressed({
        triggerEvent: armedTrigger,
        reason: 'already_shown_in_session',
      });
      return;
    }

    if (!hasDelayElapsed || !isEligibilityResolved) {
      return;
    }

    if (!isQuestionEligible) {
      hasRecordedSuppression.current = true;
      trackOabCaptureTriggerSuppressed({
        triggerEvent: armedTrigger,
        reason: 'not_eligible',
      });
      return;
    }

    if (isCsatModalOpen) {
      hasRecordedSuppression.current = true;
      trackOabCaptureTriggerSuppressed({
        triggerEvent: armedTrigger,
        reason: 'csat_open',
      });
    }
  }, [
    armedTrigger,
    isOpen,
    hasDelayElapsed,
    isEligibilityResolved,
    isQuestionEligible,
    isCsatModalOpen,
  ]);

  const close = useCallback((): void => setIsDismissed(true), []);

  const { answer: recordAnswer, hasAnswerFailed, isAnswering } =
    useOabCaptureAnswer({
      surface: SURFACE,
      answerProfession,
      onAnswered: (isLawyer) => setAnswer(isLawyer ? 'lawyer' : 'other'),
    });

  const dismiss = useCallback(
    (method: 'x' | 'esc'): void => {
      // Fechar por cima do PATCH desmontava o card com a resposta em voo: a
      // profissão era gravada, o formulário que o usuário acabou de pedir
      // desaparecia e saíam `lawyer_answered` e `dismissed` pela mesma
      // interação — a mesma guarda que o banner do M2 já tem.
      if (isAnswering()) {
        return;
      }

      trackOabCaptureDismissed({ surface: SURFACE, method, step: 'question' });
      recordShown();
      setIsDismissed(true);
    },
    [recordShown, isAnswering],
  );

  // Os cards da resposta vêm antes de `isOpen`: ele reflete a elegibilidade, que
  // é invalidada a cada resposta e a cada cadastro, e um falso no meio do fluxo
  // remontava o modal de cadastro e zerava o formulário. Quem fecha é `close`.
  if (isDismissed) {
    return null;
  }

  if (answer === 'lawyer') {
    return (
      <OabRegistrationModal
        surface={SURFACE}
        entryStep="question"
        onClose={close}
      />
    );
  }

  if (answer === 'other') {
    return <OabThanksModal onClose={close} />;
  }

  if (!isOpen) {
    return null;
  }

  return (
    // Foco preso no card, foco inicial, devolução do foco ao fechar, lock de
    // scroll e Esc vêm do Dialog; só o clique no backdrop segue ignorado, para
    // não transformar clique acidental em dispensa.
    <ModalDialog
      open
      width="500px"
      overflow="hidden"
      aria-labelledby={TITLE_ID}
      onClose={(_event: unknown, reason: string) =>
        reason === 'escapeKeyDown' && dismiss('esc')
      }
    >
      <S.Card>
        <S.Illustration src={illustration} alt="" />
        <S.CloseButton
          type="button"
          aria-label="Fechar"
          disabled={isAnsweringProfession}
          onClick={() => dismiss('x')}
        >
          ×
        </S.CloseButton>

        <S.Body>
          <S.Title id={TITLE_ID}>Você é advogado?</S.Title>
          <S.Description>
            Cadastre sua OAB na Jusfy para monitorar todos os seus processos de
            forma gratuita.
          </S.Description>

          {hasAnswerFailed && (
            <S.AnswerError role="alert">
              {OAB_CAPTURE_ANSWER_ERROR}
            </S.AnswerError>
          )}

          <S.Actions>
            <S.DeclineAnswer
              type="button"
              disabled={isAnsweringProfession}
              onClick={() => recordAnswer(false)}
            >
              Não sou advogado
            </S.DeclineAnswer>
            <S.ConfirmAnswer
              type="button"
              disabled={isAnsweringProfession}
              onClick={() => recordAnswer(true)}
            >
              Sim, sou advogado
            </S.ConfirmAnswer>
          </S.Actions>
        </S.Body>
      </S.Card>
    </ModalDialog>
  );
};

export default OabCaptureValueMomentModal;
